import { useEffect, useRef } from 'react';

/**
 * Tracks window.visualViewport.height into --app-viewport-height, following the visual viewport
 * when mobile browser chrome (URL bar) or the on-screen keyboard changes it, rAF-throttled.
 * Also tracks window.visualViewport.offsetTop into --app-viewport-offset-top (falling back to 0).
 * Falls back to window.innerHeight when visualViewport is unavailable.
 */
export function useViewportHeight() {
  const rafIdRef = useRef<number | null>(null);

  useEffect(() => {
    // Feature detection for visualViewport API
    const hasVisualViewport = typeof window.visualViewport !== 'undefined';
    const listenerType = hasVisualViewport ? 'visualViewport' : 'window';

    const updateHeight = () => {
      // Get viewport height from appropriate source
      const height = window.visualViewport?.height ?? window.innerHeight;

      // Update CSS custom property
      document.documentElement.style.setProperty(
        '--app-viewport-height',
        `${height}px`
      );

      // Update CSS custom property for the visual viewport's offset from the layout viewport
      document.documentElement.style.setProperty(
        '--app-viewport-offset-top',
        `${window.visualViewport?.offsetTop ?? 0}px`
      );
    };

    const throttledHandler = () => {
      // Coalesce: if a frame is already pending, let it pick up the latest state
      if (rafIdRef.current !== null) {
        return;
      }

      // Schedule new update
      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;
        updateHeight();
      });
    };

    // Set initial value on mount
    updateHeight();

    // Attach resize (and, for visualViewport, scroll) listeners
    if (listenerType === 'visualViewport') {
      window.visualViewport?.addEventListener('resize', throttledHandler, {
        passive: true,
      });
      window.visualViewport?.addEventListener('scroll', throttledHandler, {
        passive: true,
      });
    } else {
      window.addEventListener('resize', throttledHandler, { passive: true });
    }

    // Cleanup
    return () => {
      // Remove event listeners
      if (listenerType === 'visualViewport') {
        window.visualViewport?.removeEventListener('resize', throttledHandler);
        window.visualViewport?.removeEventListener('scroll', throttledHandler);
      } else {
        window.removeEventListener('resize', throttledHandler);
      }

      // Cancel pending animation frame
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }

      // Remove CSS properties
      document.documentElement.style.removeProperty('--app-viewport-height');
      document.documentElement.style.removeProperty(
        '--app-viewport-offset-top'
      );
    };
  }, []); // Empty dependency array - runs once on mount
}
